import { useState } from 'react';

interface CrossSectionExplorerProps {
  solidId: string;
  name: string;
  crossSections: {
    horizontal: string;
    vertical: string;
    diagonal: string;
  };
}

export default function CrossSectionExplorer({ solidId, name, crossSections }: CrossSectionExplorerProps) {
  const [selectedSlice, setSelectedSlice] = useState<'horizontal' | 'vertical' | 'diagonal'>('horizontal');

  const getSliceVisualization = () => {
    const sliceType = selectedSlice;
    const result = crossSections[sliceType];

    let color = '';
    let icon = '';

    if (result.toLowerCase().includes('circle')) {
      color = 'bg-rose-400';
      icon = 'ri-checkbox-blank-circle-fill';
    } else if (result.toLowerCase().includes('rectangle') || result.toLowerCase().includes('square')) {
      color = 'bg-sky-400';
      icon = 'ri-checkbox-blank-fill';
    } else if (result.toLowerCase().includes('triangle')) {
      color = 'bg-amber-400';
      icon = 'ri-play-fill';
    } else if (result.toLowerCase().includes('ellipse') || result.toLowerCase().includes('oval')) {
      color = 'bg-purple-400';
      icon = 'ri-checkbox-blank-circle-fill';
    } else {
      color = 'bg-teal-400';
      icon = 'ri-shape-fill';
    }

    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className={`w-32 h-32 ${color} rounded-lg flex items-center justify-center shadow-lg mb-4`}>
          <i className={`${icon} text-6xl text-white`}></i>
        </div>
        <p className="text-lg font-bold text-slate-800">{result}</p>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
      <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
        <i className="ri-scissors-cut-line text-xl"></i>
        Cross-Section Explorer
      </h3>
      <p className="text-sm text-slate-600 mb-6">
        See what 2D shape you get when slicing the {name} in different directions
      </p>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setSelectedSlice('horizontal')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
            selectedSlice === 'horizontal'
              ? 'bg-teal-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <i className="ri-subtract-line mr-2"></i>
          Horizontal
        </button>
        <button
          onClick={() => setSelectedSlice('vertical')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
            selectedSlice === 'vertical'
              ? 'bg-teal-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <i className="ri-divide-line mr-2"></i>
          Vertical
        </button>
        <button
          onClick={() => setSelectedSlice('diagonal')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
            selectedSlice === 'diagonal'
              ? 'bg-teal-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <i className="ri-slash-commands-2 mr-2"></i>
          Diagonal
        </button>
      </div>

      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-6 border-2 border-slate-200">
        {getSliceVisualization()}
      </div>

      <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
        <p className="text-xs text-amber-900 flex items-start gap-2">
          <i className="ri-lightbulb-line text-base mt-0.5"></i>
          <span>
            <strong>Tip:</strong> Cross-sections help us understand 3D shapes by examining their 2D slices. This concept is used in medical imaging (CT scans) and engineering design!
          </span>
        </p>
      </div>
    </div>
  );
}